/**
 * The challenge surface — the Human Decision Point, on screen (Sprint 09 F4; ADR-0038; canon
 * `challenge_rescore_requirements.md` R1–R9).
 *
 * Two pieces, deliberately separate:
 *   • {@link ChallengeButton} — the PER-FINDING affordance that sits on each category row. R1 is explicit
 *     that this must be per-finding and not a general feedback box, because a feedback box collects
 *     opinions while a per-finding challenge collects an argument about a specific number.
 *   • {@link ChallengeLedgerCard} — every challenge and its answer, open ones first, with the re-score
 *     delta shown in full.
 *
 * ## Voice and reach (ADR-0037 workstream E; the PEE usability ruling R8–R11)
 *
 * The reader is a construction professional, not an analyst: plain words, no jargon, no raw error codes,
 * and the conclusion is always a recommendation rather than a verdict. Filing takes two interactions
 * (open the form, send it) and answering takes three at most.
 *
 * ## What the client can see, and what it cannot make up
 *
 * The challenger's NAME is never sent from here — the server reads it from the session, so the record
 * names who actually acted. The score shown as "issued" is never rewritten either: a re-score is
 * recorded against the answer, so this panel can show both numbers and the argument between them.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  answerChallenge,
  correctedDscrFloorOf,
  fileChallenge,
  getChallenges,
  type Challenge,
  type ChallengeCorrection,
  type ChallengeLedger,
  type ChallengeResolution,
  type Rescore,
  type RescoreWithheld,
} from '../api.js';
import { timestamp } from '../format.js';

/** What the report needs to know to offer challenges at all. Absent → the report renders read-only. */
export interface ChallengeContext {
  analysisId: string;
  /** Whether this member may ANSWER (owner/admin). Filing is open to everyone (R2). */
  canAnswer: boolean;
  ledger: ChallengeLedger;
  /** Re-read the ledger after a change. */
  reload: () => void;
}

/**
 * Load and hold the challenge ledger for a run. Returns `null` until the first read completes, so a
 * caller renders nothing rather than an empty ledger that later fills in.
 */
export function useChallenges(
  analysisId: string | null,
  canAnswer: boolean,
): ChallengeContext | null {
  const [ledger, setLedger] = useState<ChallengeLedger | null>(null);

  const reload = useCallback(() => {
    if (!analysisId) return;
    void getChallenges(analysisId).then(setLedger);
  }, [analysisId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!analysisId || !ledger) return null;
  return { analysisId, canAnswer, ledger, reload };
}

/** The five categories whose tier a correction may replace — the server refuses the rest, so the form
 * offers a corrected score only where one can actually be applied (`sponsorExecution` is composed from
 * the sponsor assessment, not picked from a tier). */
const CORRECTABLE = new Set([
  'locationMarket',
  'financialUnderwriting',
  'capitalStack',
  'productDesign',
  'exitStrategy',
]);

const TARGET_LABEL: Record<string, string> = {
  locationMarket: 'Location & market',
  financialUnderwriting: 'Financial underwriting',
  sponsorExecution: 'Sponsor & execution',
  capitalStack: 'Capital stack',
  productDesign: 'Product & design',
  exitStrategy: 'Exit strategy',
};

/** What a challenge is against, in words. A standard challenge names the rule it contests. */
export function targetLabel(c: Pick<Challenge, 'kind' | 'target'>): string {
  if (c.kind === 'standard') return `Scoring standard — ${c.target}`;
  return TARGET_LABEL[c.target] ?? c.target;
}

const RESOLUTION_VIEW: Record<ChallengeResolution, { label: string; tone: string }> = {
  accepted: { label: 'Accepted', tone: 'fixable' },
  partial: { label: 'Partly accepted', tone: 'partial' },
  rejected: { label: 'Not accepted', tone: 'structural' },
};

/**
 * The re-score in one plain sentence. Exported for a direct unit test: the sign and the band wording are
 * the two things a reader will quote back, so they are worth pinning.
 */
export function rescoreSummary(r: Rescore): string {
  const direction = r.delta > 0 ? 'up' : r.delta < 0 ? 'down' : 'unchanged at';
  const magnitude =
    r.delta === 0 ? '' : ` ${Math.abs(r.delta)} point${Math.abs(r.delta) === 1 ? '' : 's'}`;
  // ⚠️ FROM and TO, the way the score itself is reported. This said only where the recommendation
  // moved TO — while the very same sentence showed the score as `51 → 63`. A verdict is the part a
  // reader acts on, so reporting it with half the movement made the smaller number more auditable
  // than the bigger conclusion. `priorRecommendation` was on the payload the whole time and nothing
  // read it (found by the orphan-field sweep, #683).
  //
  // Falls back to the old wording when the prior is absent: runs written before the field carry no
  // prior, and inventing one would be worse than the shorter sentence.
  const band =
    r.recommendationChanged && r.newRecommendation
      ? r.priorRecommendation
        ? ` The recommendation moved from ${r.priorRecommendation.toLowerCase()} to ${r.newRecommendation.toLowerCase()}.`
        : ` The recommendation moved to ${r.newRecommendation.toLowerCase()}.`
      : '';
  return `Score ${direction}${magnitude}: ${r.priorScore} → ${r.newScore}.${band}`;
}

/**
 * The DSCR floor the corrected deal was scored against, in one line (#1415) — or null when the re-score
 * did not re-resolve it (the floor did not change) or the field cannot be read. Exported for a direct
 * unit test, like {@link rescoreSummary}.
 *
 * ⚠️ "National default" is said ONLY on the national-default basis. A run whose state was not recorded
 * can also be corrected onto a MARKET-RATE floor (a product-type fix on an old conventional run); that
 * floor never reads the state, so telling the reader it fell to the national default would be false.
 */
export function correctedFloorLine(r: Rescore): string | null {
  const f = correctedDscrFloorOf(r);
  if (!f) return null;
  const floor = `${f.floor.toFixed(2)}x`;
  if (f.stateOnRun === 'not-recorded' && f.basis === 'national-default') {
    return `DSCR floor for the corrected deal: ${floor} — state not recorded on this run — national default; verify against the state agency.`;
  }
  return `DSCR floor for the corrected deal: ${floor} — ${f.label}.`;
}

/**
 * What a reader is told when a stored delta is no longer this deal's history (ADR-0038 R7). Exported
 * alongside {@link rescoreSummary} and for the same reason — this is the sentence a client would quote.
 *
 * It names the remedy, because a reader who is told only that a number was withheld cannot act. It does
 * NOT repeat the withheld prior: printing "it said 51" is printing the very figure the deal was never
 * issued, which is what R7 exists to stop.
 */
export function rescoreWithheldSummary(w: RescoreWithheld): string {
  const issued =
    w.reason === 'superseded' && typeof w.issuedScore === 'number'
      ? ` This deal was issued at ${w.issuedScore}.`
      : '';
  return `A score change was recorded for this answer, but it was calculated under a scoring standard that has since changed, so it is not shown.${issued} Re-run the deal to see the effect of this correction under the current standard.`;
}

/**
 * Whether an answer can be sent as written — R9, at the surface. The route and the database refuse an
 * empty reason too; this exists so the button is visibly disabled rather than the click failing, which
 * is the difference between a rule the user understands and one that just annoys them.
 */
export function canSubmitAnswer(reasoning: string): boolean {
  return reasoning.trim().length > 0;
}

// ── The per-finding affordance (R1) ─────────────────────────────────────────────────────────────────

export function ChallengeButton({
  ctx,
  target,
  label,
}: {
  ctx: ChallengeContext;
  target: string;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [claim, setClaim] = useState('');
  const [docId, setDocId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openCount = ctx.ledger.challenges.filter(
    (c) => c.kind === 'finding' && c.target === target && !c.answer,
  ).length;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await fileChallenge(ctx.analysisId, {
        kind: 'finding',
        target,
        claim,
        supportingDocumentId: docId.trim() === '' ? null : docId.trim(),
      });
      setClaim('');
      setDocId('');
      setOpen(false);
      ctx.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We couldn’t file that just now.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="chal__open" onClick={() => setOpen(true)}>
        Challenge this
        {openCount > 0 && <span className="chal__count">{openCount} open</span>}
      </button>
    );
  }

  return (
    <div className="chal__form">
      <label className="chal__label" htmlFor={`chal-claim-${target}`}>
        What’s wrong with the {label.toLowerCase()} finding?
      </label>
      <textarea
        id={`chal-claim-${target}`}
        className="chal__textarea"
        rows={3}
        value={claim}
        placeholder="Say what the analysis got wrong and what the right answer is."
        onChange={(e) => setClaim(e.target.value)}
      />
      <label className="chal__label" htmlFor={`chal-doc-${target}`}>
        Backing document (optional)
      </label>
      <input
        id={`chal-doc-${target}`}
        className="chal__input"
        value={docId}
        placeholder="Document reference, if you have one"
        onChange={(e) => setDocId(e.target.value)}
      />
      <p className="chal__hint">
        A document isn’t required. With one, the correction is recorded as verified; without one,
        it’s recorded on your name.
      </p>
      {error && <p className="chal__error">{error}</p>}
      <div className="chal__actions">
        <button
          type="button"
          className="chal__send"
          disabled={busy || claim.trim().length === 0}
          onClick={() => void send()}
        >
          {busy ? 'Filing…' : 'File challenge'}
        </button>
        <button type="button" className="chal__cancel" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── The ledger (R3/R4/R5) ───────────────────────────────────────────────────────────────────────────

export function ChallengeLedgerCard({ ctx }: { ctx: ChallengeContext }) {
  const { challenges, openCount } = ctx.ledger;
  return (
    <section className="card chal">
      <h3 className="card__title">
        Challenges
        {openCount > 0 && <span className="chip chip--partial chal__badge">{openCount} open</span>}
      </h3>
      {/* R7 — the other side of the boundary. Arguing that a RULE is wrong is a different act from
          arguing that this deal was scored wrong, so it gets its own affordance and its own queue. */}
      <StandardChallengeForm ctx={ctx} />
      {challenges.length === 0 ? (
        <p className="card__prose">
          Nothing has been challenged yet. Every finding in the Findings section can be argued with
          — open it, use “Challenge this” on the one you disagree with, and the argument and its
          answer stay on this deal.
        </p>
      ) : (
        <ol className="chal__list">
          {challenges.map((c) => (
            <ChallengeRow key={c.id} ctx={ctx} challenge={c} />
          ))}
        </ol>
      )}
    </section>
  );
}

/**
 * Raise a challenge against the SCORING STANDARD. It is filed against this deal — that is where the
 * argument arose and what fixes its version (R7) — but it never re-scores the deal, and it collects on
 * the standards queue for a future version instead.
 */
function StandardChallengeForm({ ctx }: { ctx: ChallengeContext }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [claim, setClaim] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await fileChallenge(ctx.analysisId, { kind: 'standard', target: target.trim(), claim });
      setTarget('');
      setClaim('');
      setOpen(false);
      ctx.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We couldn’t file that just now.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="chal__open chal__open--standard"
        onClick={() => setOpen(true)}
      >
        The rule itself is wrong
      </button>
    );
  }

  return (
    <div className="chal__form">
      <label className="chal__label" htmlFor="chal-std-target">
        Which rule or threshold?
      </label>
      <input
        id="chal-std-target"
        className="chal__input"
        value={target}
        placeholder="e.g. the minimum debt coverage"
        onChange={(e) => setTarget(e.target.value)}
      />
      <label className="chal__label" htmlFor="chal-std-claim">
        What’s wrong with it?
      </label>
      <textarea
        id="chal-std-claim"
        className="chal__textarea"
        rows={3}
        value={claim}
        placeholder="Say why the rule is set wrong, and what it should be."
        onChange={(e) => setClaim(e.target.value)}
      />
      <p className="chal__hint">
        This won’t change this deal’s score. A rule changes only by version, and then it changes for
        every deal at once — so this goes to the scoring-standard queue.
      </p>
      {error && <p className="chal__error">{error}</p>}
      <div className="chal__actions">
        <button
          type="button"
          className="chal__send"
          disabled={busy || target.trim().length === 0 || claim.trim().length === 0}
          onClick={() => void send()}
        >
          {busy ? 'Filing…' : 'Raise it'}
        </button>
        <button type="button" className="chal__cancel" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ChallengeRow({ ctx, challenge }: { ctx: ChallengeContext; challenge: Challenge }) {
  return (
    <li className={`chal__row chal__row--${challenge.status}`}>
      <div className="chal__head">
        <span className="chal__target">{targetLabel(challenge)}</span>
        {challenge.answer ? (
          <span
            className={`chip chip--${RESOLUTION_VIEW[challenge.answer.resolution].tone} chal__chip`}
          >
            {RESOLUTION_VIEW[challenge.answer.resolution].label}
          </span>
        ) : (
          <span className="chip chip--partial chal__chip">Open</span>
        )}
      </div>
      <p className="chal__claim">{challenge.claim}</p>
      <p className="chal__meta muted">
        Raised by {challenge.challengerName} · {timestamp(challenge.createdAt)}
        {challenge.supportingDocumentId ? ' · document attached' : ''}
        {/*
          WHICH scoring standard this challenge was raised against. It was served on every challenge
          from the day the ledger shipped and rendered nowhere — invisible to `orphan:fields` because
          `standardVersion` IS read elsewhere, on a DIFFERENT interface (`AnalysisReport`'s prop, fed
          from `run.scoringStandardVersion`). A type-aware sweep found it; see PR #690.

          It belongs on the row because calibration is versioned deliberately here: a ruling that
          moves a weight is a DATED ENTRY, never an edit. So a challenge answered under 2.7.9 while
          head is 2.7.14 was judged against a basis that has since moved — and without the version on
          the row, an old challenge and a current one are indistinguishable.
        */}
        {challenge.standardVersion ? ` · scoring standard ${challenge.standardVersion}` : ''}
      </p>

      {challenge.answer ? (
        <div className="chal__answer">
          <p className="chal__reason">{challenge.answer.reasoning}</p>
          {challenge.answer.rescore && (
            <>
              <p className="chal__rescore">{rescoreSummary(challenge.answer.rescore)}</p>
              {/* #1415 — WHICH floor the corrected deal was held to, when the correction changed it.
                  A Financial Underwriting move caused by a floor change otherwise shows with no reason,
                  and a floor that fell to the national default (state not recorded) is never seen. */}
              {(() => {
                const line = correctedFloorLine(challenge.answer.rescore);
                return line ? <p className="chal__rescore chal__floor">{line}</p> : null;
              })()}
              {/* WHICH categories moved — R5 asks for "prior, new, the delta, and every finding that
                  moved", and the sentence above covers only the first three. A total that changed by
                  four points tells a reader the score moved; it does not tell them WHERE, which is the
                  part they can check against their own deal. Served since the rescore shipped and
                  rendered by nothing until the orphan-field sweep found it (#683).

                  Rendered only when the server sent it — an empty list would be a claim that nothing
                  moved, and absent means the run predates the field. */}
              {(challenge.answer.rescore.changedCategories?.length ?? 0) > 0 && (
                <ul className="chal__deltas">
                  {challenge.answer.rescore.changedCategories?.map((c) => (
                    <li key={c.category}>
                      {c.category}: {c.priorScore} → {c.newScore}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {challenge.answer.rescoreWithheld && (
            <p className="chal__rescore chal__rescore--withheld">
              {rescoreWithheldSummary(challenge.answer.rescoreWithheld)}
            </p>
          )}
          <p className="chal__meta muted">
            Answered by {challenge.answer.answeredByName} · {timestamp(challenge.answer.answeredAt)}
            {challenge.answer.provenanceTier
              ? ` · recorded as ${challenge.answer.provenanceTier}`
              : ''}
          </p>
        </div>
      ) : ctx.canAnswer ? (
        <AnswerForm ctx={ctx} challenge={challenge} />
      ) : (
        <p className="chal__meta muted">Waiting on an answer from an owner.</p>
      )}
    </li>
  );
}

function AnswerForm({ ctx, challenge }: { ctx: ChallengeContext; challenge: Challenge }) {
  const [resolution, setResolution] = useState<ChallengeResolution>('accepted');
  const [reasoning, setReasoning] = useState('');
  const [points, setPoints] = useState('');
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A correction is offered only where one can actually be applied: an accepted challenge, against a
  // finding, on a category whose tier is selected rather than composed. Everywhere else the answer is
  // recorded on its reasoning alone — which is a complete answer, not a degraded one.
  const correctable =
    resolution !== 'rejected' && challenge.kind === 'finding' && CORRECTABLE.has(challenge.target);
  const wantsCorrection = correctable && points.trim() !== '' && evidence.trim() !== '';

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const correction: ChallengeCorrection | null = wantsCorrection
        ? {
            kind: 'selection',
            category: challenge.target,
            selection: { points: Number(points), evidence: evidence.trim() },
          }
        : null;
      await answerChallenge(ctx.analysisId, challenge.id, { resolution, reasoning, correction });
      ctx.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We couldn’t record that just now.');
      setBusy(false);
    }
  }

  return (
    <div className="chal__form chal__form--answer">
      <div
        className="chal__choices"
        role="group"
        aria-label="How are you answering this challenge?"
      >
        {(['accepted', 'partial', 'rejected'] as ChallengeResolution[]).map((r) => (
          <button
            key={r}
            type="button"
            className={`chal__choice${resolution === r ? ' chal__choice--on' : ''}`}
            aria-pressed={resolution === r}
            onClick={() => setResolution(r)}
          >
            {RESOLUTION_VIEW[r].label}
          </button>
        ))}
      </div>

      <label className="chal__label" htmlFor={`chal-reason-${challenge.id}`}>
        Why? (required)
      </label>
      <textarea
        id={`chal-reason-${challenge.id}`}
        className="chal__textarea"
        rows={3}
        value={reasoning}
        placeholder="Say what you checked and what you concluded."
        onChange={(e) => setReasoning(e.target.value)}
      />

      {correctable && (
        <>
          <label className="chal__label" htmlFor={`chal-points-${challenge.id}`}>
            Corrected score for this finding (optional)
          </label>
          <div className="chal__correction">
            <input
              id={`chal-points-${challenge.id}`}
              className="chal__input chal__input--points"
              inputMode="numeric"
              value={points}
              placeholder="Points"
              onChange={(e) => setPoints(e.target.value)}
            />
            <input
              className="chal__input"
              value={evidence}
              placeholder="What supports the corrected score"
              onChange={(e) => setEvidence(e.target.value)}
            />
          </div>
          <p className="chal__hint">
            Leave these blank to record your answer without changing the score. Fill them in and the
            deal re-scores here, with the old score kept alongside the new one.
          </p>
        </>
      )}

      {error && <p className="chal__error">{error}</p>}
      <div className="chal__actions">
        <button
          type="button"
          className="chal__send"
          disabled={busy || !canSubmitAnswer(reasoning)}
          onClick={() => void send()}
        >
          {busy ? 'Recording…' : 'Record answer'}
        </button>
      </div>
    </div>
  );
}
